import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { centreLogoNote as centreLogoNoteFor, centreLogoUploadError } from "./centreLogoNote";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ApiError,
  type FirstRunRequirement,
  type GeneratedNodeAttribute,
  type GeneratedStatus,
  type InstanceCalendarSourceView,
  type InstanceCustomisationView,
  type InstanceDetail,
  type InstanceSummary,
  type ItemStatusEvent,
  type NotificationSubscriptionDto,
  type SegmentStatusEvent,
  type SetNodeAttributeRequest,
  type WelcomeAction,
  welcomeHasContent,
  type ItemSpan,
} from "@keydials/api-client";
import {
  buildDialVM,
  DialWorkspace,
  DialWorkspaceProvider,
  FollowPrompt,
  hasEmptyNodes,
  Icon,
  type DialWorkspaceCapabilities,
  type DialWorkspaceController,
  type ItemEditPolicy,
  type ReviewScheduleInput,
  type WorkspaceServices,
  segmentNoun,
  itemNouns,
  dialLabel,
  listTabLabel,
  audienceAllows,
  responsibleAt,
  responsibleContextOf,
  coveringAssignment,
  isAssignee,
} from "@keydials/dial-workspace";
import { api, getAccessToken, useAuth } from "../auth/AuthContext";
import { CALENDARS_SYNCED_EVENT, CALENDAR_REFRESH_LOOK_AGAIN_MS } from "../lib/calendarSync";
import { DialBar } from "../components/chrome/DialBar";
import { FirstRunCapture } from "../components/FirstRunCapture";
import { InsightsPanel } from "../workspace/InsightsPanel";
import { EditStructurePanel } from "../workspace/EditStructurePanel";
import { UpgradeNotice } from "../workspace/UpgradeNotice";
import { AddPeoplePrompt } from "../workspace/AddPeoplePrompt";
import { GettingStartedButton, WelcomePage } from "../workspace/WelcomePage";
import { welcomeActionUsable, welcomeStep } from "../workspace/welcomeActions";
import { setWelcomeOpener } from "../workspace/welcomeSlot";
import { CalendarSetupPrompt } from "../workspace/CalendarSetupPrompt";
import { AddOnDayDialog, type AppointmentBeingChanged } from "../workspace/AddOnDayDialog";
import { appointmentBeingChanged, appointmentSources, isDaySegmentKey, slotStartOf } from "../workspace/addOnDay";
import { OrgNamePrompt, useOrgNamePrompt } from "../components/OrgNamePrompt";
import { shouldOfferAddPeople } from "../workspace/addPeopleOffer";
import { isSimulatableRole, viewRoles } from "../workspace/checkingRole";
import { TestingNotice } from "../workspace/TestingNotice";
import { TestCopyActions } from "../workspace/TestCopyActions";
import { DemoNotice } from "../workspace/DemoNotice";
import { AutoLockPanel } from "../workspace/AutoLockPanel";
import { TimeZonePanel } from "../workspace/TimeZonePanel";
import { DialNamePanel } from "../workspace/DialNamePanel";
import { DialSettingsPanel } from "../settings/DialSettingsPanel";
import { SettingsCard } from "../settings/SettingsCard";
import { dialSettingsItems, resolveSection } from "../settings/dialSettingsNav";
import { shareRefusal, type ShareChannelName } from "../settings/grantFeedback";
import { MembersSection } from "../settings/MembersSection";
import { WhosWhoSection } from "../settings/WhosWhoSection";
import { DaysLayoutCard } from "../settings/DaysLayoutCard";
import { ContactsSection } from "../settings/ContactsSection";
import { CalendarsSection } from "../settings/CalendarsSection";
import { HiddenRevealBanner, HiddenSectionsNote } from "../workspace/HiddenSectionsNote";
import { LicensingSection } from "../settings/LicensingSection";
import { FamilyCard } from "../components/FamilyCard";
import { GuestsPanel } from "../components/chrome/GuestsPanel";
import { StatusRolesPanel } from "../components/chrome/modals";
import { FeedsSettingsPanel } from "../components/chrome/FeedsPanel";
import { DisplaySection } from "../settings/DisplaySection";
import { useWorkspaceMode } from "../workspace/WorkspaceModeProvider";
import { useHelp } from "../help/HelpController";
import { canSetAnyStatus } from "../help/audience";
import { kioskNav } from "../lib/kiosk";
import type { HelpDialKind } from "../help/types";
import { useIdleLock } from "../lib/useIdleLock";
import { useRealtimeGroup } from "../realtime/useRealtime";
import { groups } from "../realtime/groups";
import { createLoadSequencer, startWorkspaceLoad } from "./workspaceLoad";
import { findItemStatus, findSegmentStatus, mergeItemStatus, mergeNodeAttributeValue, mergeSectionStatus, mergeSegmentStatus, removeItemStatus, removeSegmentStatus } from "./optimistic";

// Host services for the shared workspace surface: real API + access token, and the segment-discussion
// realtime subscription wired to this app's SignalR hook. Module-level so the context value stays stable.
const workspaceServices: WorkspaceServices = {
  api,
  getAccessToken,
  useSegmentRealtime: (instanceId, sectionKey, segmentKey, handlers) =>
    useRealtimeGroup(groups.segment(instanceId, sectionKey, segmentKey), handlers),
  // The section and whole-dial Discussion transcripts (KEY-559) span many action points, so they listen on
  // the instance group, which carries every scope's events, and pick out the message ones themselves.
  useInstanceRealtime: (instanceId, handlers) => useRealtimeGroup(groups.instance(instanceId), handlers),
};

// A rejected status change. A 400 here is a rule the user can act on — most often a required field that
// still has to be filled in (KEY-248) — so the server's own wording is shown rather than swallowed.
function statusChangeMessage(e: unknown): string {
  if (e instanceof ApiError && e.status === 403) return "You don't have permission to set that status.";
  if (e instanceof ApiError && e.status === 400 && e.message) return e.message;
  return "Could not change the status.";
}

// The end-user app is fully live: every feature is interactive and persisted. `workspace` is the one field
// that varies per PERSON rather than per host, so it is filled in below from their stored choice (KEY-531).
const LIVE_CAPABILITIES: Omit<DialWorkspaceCapabilities, "workspace" | "pushNotifications" | "tabStrip" | "onTabStripChange" | "lookBackCompare" | "lookAhead"> = {
  mode: "live",
  documents: true,
  messages: true,
  subscriptions: true,
  itemEditing: true,
  groupDrillIn: true,
};

// What a navigation into a dial may carry — see where it is read, below.
type WorkspaceNavState = {
  organisationId?: string;
  groupId?: string;
  groupName?: string;
  fromParentInstanceId?: string;
  fromParentName?: string;
  focusSectionKey?: string;
  focusSegmentKey?: string;
};

// A value held against the dial it was fetched for, and read back only for that dial (see `shown` below).
type ForDial<T> = { id: string; value: T };
function forDial<T>(held: ForDial<T> | null, dialId: string | null | undefined, fallback: T): T {
  return held && held.id === dialId ? held.value : fallback;
}
// Stable empties, so a dial with nothing loaded yet doesn't hand the workspace a fresh array every render.
const NO_SEGMENT_EVENTS: SegmentStatusEvent[] = [];
const NO_ITEM_EVENTS: ItemStatusEvent[] = [];
const NO_SUBSCRIPTIONS: NotificationSubscriptionDto[] = [];

// While the next dial loads (`stale`), the last one stays where it is, dimmed, with this pill over it. Both
// wait a beat before showing, so a quick load goes straight to the new dial instead of flashing.
const STALE_DIAL_STYLE: CSSProperties = { opacity: 0.45, pointerEvents: "none", transition: "opacity .2s ease .15s" };
const LOADING_PILL_STYLE: CSSProperties = {
  position: "fixed",
  top: "42%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 40,
  padding: "10px 18px",
  borderRadius: 999,
  background: "var(--surface)",
  border: "var(--border-w) solid var(--border)",
  boxShadow: "var(--shadow-md)",
  color: "var(--text-muted)",
  fontSize: 14,
  fontWeight: 600,
  animation: "kd-fade .2s ease .15s both",
};

/**
 * `instanceId` is supplied by a CONFINED session's router (kiosk PIN / guest), whose route is a literal
 * path rather than `/instances/:id` so another dial's URL can never match it. That means `useParams()`
 * yields nothing there, so the id has to arrive as a prop — without it every loader below bails on its
 * `!id` guard and the dial sits on "Loading…" forever. An ordinary session omits it and the param wins.
 */

/** A dial-level tab, as DialWorkspace takes it — what a welcome-page button can switch to (KEY-814). */
type WorkspaceTab = NonNullable<Parameters<typeof DialWorkspace>[0]["initialPanelTab"]>;

export function Workspace({ instanceId }: { instanceId?: string } = {}) {
  const { id: routeId, section: routeSection } = useParams<{ id: string; section: string }>();
  const id = instanceId ?? routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const { me, lockTo, guestInstanceId } = useAuth();
  // KEY-743: "is this the right name for your organisation?" — asked before the dial's other one-off prompts.
  const orgNamePrompt = useOrgNamePrompt();

  // The dial's owning org may differ from the token's active org (cross-org access, KEY-132). The Dashboard
  // hands it over in navigation state; on a direct URL we resolve it from the portfolio in `load`. Setting it
  // on the api client scopes every request for this dial — detail, history, status writes, messages — into
  // the owning org. Cleared on unmount so the next view starts unscoped.
  // When a dial is opened from a group, that group's id + name ride along in the same state so the
  // breadcrumb can offer a step back to the group (My Dials › Group › Dial). Group Key Dial drill-ins
  // carry the parent instance the same way (fromParent*), and additionally name the part of the child to
  // land on: the section the clicked node stood for (focusSectionKey) and, for a mirror-face segment that
  // mirrors one action point, that action point (focusSegmentKey).
  const navState = location.state as WorkspaceNavState | null;
  // KEY-916: an emailed link lands on the place it is about — ?section=&segment= (an allocation, a notification).
  const linkTarget = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return { section: q.get("section") || null, segment: q.get("segment") || null };
  }, [location.search]);
  const navOrgId = navState?.organisationId ?? null;
  // A drill-in names the parent it came from, which is how a child is known before anything is fetched — and
  // a child is never in the portfolio, so its load doesn't ask for one.
  const drillIn = !!navState?.fromParentInstanceId;
  // Read by `load` as it starts, to remember what the dial on screen was opened under, without making every
  // change to the state a reason to reload.
  const navStateRef = useRef(navState);
  navStateRef.current = navState;
  useEffect(() => {
    if (navOrgId) {
      api.setActiveOrg(navOrgId);
    }
    return () => api.setActiveOrg(null);
  }, [navOrgId]);

  // KEY-498: the role a checker has asked to be evaluated as, sent as a header on every call from here on.
  // What the SERVER did with it is `detail.simulatedRole`; this is only the request. Cleared on the way out
  // for the same reason setActiveOrg is — the header is global on the api-client, so leaving it set would
  // render other checking dials' cards on the Dashboard simulated too.
  //
  // A role is not persisted across a reload, deliberately: an impersonation you did not choose this session
  // is indistinguishable from a broken permission. It doesn't follow you to another dial for the same reason —
  // done by holding the request against the dial it was made on, so the next dial reads "no role" in the very
  // render its id arrives. Resetting it in an effect landed a render later: leaving a simulated dial fetched
  // the next one twice, the first time with the old role header still set.
  const [roleRequest, setRoleRequest] = useState<{ id: string | undefined; role: string | null } | null>(null);
  const requestedRole = roleRequest && roleRequest.id === id ? roleRequest.role : null;
  const [simulationBusy, setSimulationBusy] = useState(false);
  const changeSimulatedRole = useCallback((role: string | null) => {
    setSimulationBusy(true);
    setRoleRequest({ id, role });
  }, [id]);
  useEffect(() => {
    api.setCheckingRole(requestedRole);
    return () => api.setCheckingRole(null);
  }, [requestedRole]);

  // KEY-939: a demo's admin puts it back to a fresh copy of its template. The old instance is gone the moment
  // the server answers, so the only sensible place to be is the new one.
  const [resettingDemo, setResettingDemo] = useState(false);
  const [resetDemoError, setResetDemoError] = useState<string | null>(null);
  const resetDemo = useCallback(async () => {
    const token = getAccessToken();
    if (!token || !id) return;
    if (!window.confirm("Put this demo back to how it arrived? Everything entered since is discarded. The people with a place on it keep it.")) return;
    setResettingDemo(true);
    setResetDemoError(null);
    try {
      const fresh = await api.resetDemoInstance(token, id);
      navigate(`/instances/${fresh.id}`, { replace: true, state: location.state });
    } catch (e) {
      setResetDemoError(e instanceof ApiError && e.message ? e.message : "Could not reset the demo.");
    } finally {
      setResettingDemo(false);
    }
  }, [id, navigate, location.state]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    api.insightsStatus(token).then((s) => setInsightsEnabled(s.enabled)).catch(() => setInsightsEnabled(false));
    api.itemImportStatus(token).then((s) => setItemImportEnabled(s.enabled)).catch(() => setItemImportEnabled(false));
    api.peopleStatus(token).then((s) => setPeopleEnabled(s.enabled)).catch(() => setPeopleEnabled(false));
    api.pushStatus(token).then((s) => setPushEnabled(s.enabled)).catch(() => setPushEnabled(false));
  }, []);

  // KEY-651: a family pack's places live on the dial. Asked only when the dial is in the caller's OWN
  // organisation (not opened cross-org into a customer's) and they hold billing.read — the same person the
  // endpoint answers for. A partner admin must not invite relatives onto a customer's bill, just as they
  // may not switch its plan. 204 → null → no Family entry; any failure is treated the same.
  const ownFamilyOrg =
    me?.organisationId && me.capabilities.includes("billing.read") && (!navOrgId || navOrgId === me.organisationId)
      ? me.organisationId
      : null;
  useEffect(() => {
    let cancelled = false;
    const token = getAccessToken();
    if (!ownFamilyOrg || !token) return;
    api
      .getFamily(token, ownFamilyOrg)
      .then((view) => !cancelled && setFamilyPackOrg(view ? ownFamilyOrg : null))
      .catch(() => !cancelled && setFamilyPackOrg(null));
    return () => {
      cancelled = true;
    };
  }, [ownFamilyOrg]);

  const { setDialKind, setHelpAudience, start: startGuide } = useHelp();

  // KEY-531/532: the one capability that varies per PERSON. Memoised so the provider is not handed a fresh
  // object every render, which would re-render every consumer of the context on each keystroke elsewhere.
  // KEY-684: whether the subscribe menu offers "Push to my phone" is a per-SERVER flag, learned once like the
  // insight/people probes above; off until it answers, so a failed probe never offers a channel it would refuse.
  // The tab strips' names-or-icons choice is per person too; the toggle is offered only once the server has
  // answered for it, so an older API never gets a switch it can't keep.
  const { mode: workspaceMode, tabStrip, tabStripAvailable, setTabStrip, lookBackCompare, lookAhead } = useWorkspaceMode();
  const [pushEnabled, setPushEnabled] = useState(false);
  const capabilities = useMemo<DialWorkspaceCapabilities>(
    () => ({
      ...LIVE_CAPABILITIES,
      workspace: workspaceMode === "Dynamic3D" ? "dynamic3d" : workspaceMode === "Dynamic" ? "dynamic" : "classic",
      pushNotifications: pushEnabled,
      tabStrip: tabStrip === "Icons" ? "icons" : "labels",
      onTabStripChange: tabStripAvailable
        ? (next) => void setTabStrip(next === "icons" ? "Icons" : "Labels")
        : undefined,
      lookBackCompare,
      lookAhead,
    }),
    [workspaceMode, pushEnabled, tabStrip, tabStripAvailable, setTabStrip, lookBackCompare, lookAhead],
  );

  const [detail, setDetail] = useState<InstanceDetail | null>(null);
  // The dial `detail` was fetched for, and the navigation state it was opened under. This page is not remounted
  // when the URL moves to another dial (group → child, the breadcrumb back), so until the next dial has loaded,
  // `detail` is still the last one's — that window is `stale`. It is held against the id the load asked for
  // rather than read off `detail.id`, so a link spelling the GUID in another case can't strand the page on it.
  // The ref is the same answer for code that runs after an await.
  const [shown, setShown] = useState<{ id: string; nav: WorkspaceNavState | null } | null>(null);
  const shownId = useRef<string | null>(null);
  const stale = detail !== null && shown?.id !== id;
  const fresh = detail !== null && !stale;
  // KEY-539: this dial's word for an action point, for the settings panels and the messages below. Null
  // detail (still loading) falls back to the platform's word, as does a definition that declares none.
  const noun = useMemo(
    () => segmentNoun(detail),
    [detail?.segmentNounSingular, detail?.segmentNounPlural],
  );
  // Everything else the page reads is held against the dial it was fetched for, and read back only for the dial
  // on screen. The reads land independently now, so without this the next dial could paint with the last one's
  // customisation, subscriptions, timeline or summary before its own arrived — and the last dial, dimmed while
  // the next one loads, keeps its own instead of emptying out under the overlay.
  const [custFor, setCustFor] = useState<ForDial<InstanceCustomisationView | null> | null>(null);
  const [summaryFor, setSummaryFor] = useState<ForDial<InstanceSummary | null> | null>(null);
  const [historyFor, setHistoryFor] = useState<ForDial<SegmentStatusEvent[]> | null>(null);
  const [itemHistoryFor, setItemHistoryFor] = useState<ForDial<ItemStatusEvent[]> | null>(null);
  const cust = forDial(custFor, shown?.id, null);
  const summary = forDial(summaryFor, shown?.id, null);
  const history = forDial(historyFor, shown?.id, NO_SEGMENT_EVENTS);
  const itemHistory = forDial(itemHistoryFor, shown?.id, NO_ITEM_EVENTS);
  const [error, setError] = useState<string | null>(null);
  // What this dial must be told before it will open (KEY-434). Set from the server's 428 rather than
  // guessed at: the requirement lives on the definition version the dial is pinned to, so the client has
  // no way to know it is coming and no business deciding it has been met.
  const [firstRun, setFirstRun] = useState<FirstRunRequirement[] | null>(null);
  // AI insight layer (KEY-163): only shown when the server flag is on (local-only for now). Loaded once.
  const [insightsEnabled, setInsightsEnabled] = useState(false);
  const [peopleEnabled, setPeopleEnabled] = useState(false);
  // KEY-651: whether this dial's organisation is on a family pack the caller manages, which is what puts
  // Family in the dial's settings. Keyed by organisation so a stale answer never leaks onto the next dial.
  const [familyPackOrg, setFamilyPackOrg] = useState<string | null>(null);
  // AI document → items import: only offered when the server flag is on. Loaded once.
  const [itemImportEnabled, setItemImportEnabled] = useState(false);
  const [subsFor, setSubsFor] = useState<ForDial<NotificationSubscriptionDto[]> | null>(null);
  const subs = forDial(subsFor, shown?.id, NO_SUBSCRIPTIONS);

  // One load at a time, latest wins: two in-flight loads had no ordering guarantee, so a slow early
  // response could overwrite a fresher detail (our own awaited reload racing the realtime echo of the
  // same write). The sequencer makes stale responses drop — including one for the dial just left, which a
  // plain counter let through (see createLoadSequencer). The resolved owning org is cached per dial so
  // reloads skip the portfolio, and the portfolio itself is kept for the life of the page, so moving between
  // dials it lists needs no second fetch (see startWorkspaceLoad).
  const [sequencer] = useState(() => createLoadSequencer(id));
  const knownOrgId = useRef<string | null | undefined>(undefined);
  const portfolioSeen = useRef<InstanceSummary[] | undefined>(undefined);
  // Layout rather than passive, so it has run before the load effect below and before any child's effects:
  // nothing may act for the new dial while the sequencer still has the old one live.
  useLayoutEffect(() => {
    sequencer.show(id);
  }, [id, sequencer]);
  // An error or a first-run requirement was the last dial's, and would otherwise take this dial's page. Reset
  // while rendering rather than in an effect: an effect lands a commit late, and that one commit renders the
  // last dial's banner error as the next dial's whole page.
  const [errorsFor, setErrorsFor] = useState(id);
  if (errorsFor !== id) {
    setErrorsFor(id);
    setError(null);
    setFirstRun(null);
  }
  useEffect(() => {
    knownOrgId.current = undefined;
  }, [id]);

  const reloadSubs = useCallback(async () => {
    const token = getAccessToken();
    if (!token || !id) {
      return;
    }
    try {
      const next = await api.listSubscriptions(token, id);
      // Only onto the dial it was asked for: a slow answer for the last dial must not replace this one's.
      if (sequencer.isLive(id)) {
        setSubsFor({ id, value: next });
      }
    } catch {
      // best-effort; the Subscribe controls just show stale state until the next reload
    }
  }, [id, sequencer]);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token || !id) {
      return;
    }
    // Null when this is the last dial's debounced reload, or the reload one of its writes awaited, starting
    // after the URL moved on. Doing nothing is right: beginning would supersede the load of the dial now open.
    const isCurrent = sequencer.begin(id);
    if (!isCurrent) {
      return;
    }
    const openedWith = navStateRef.current;
    const firstVisit = knownOrgId.current === undefined;
    // Resolve the owning org and send every read at once: detail (gating), then history, item history,
    // customisation and subscriptions (best-effort — a supplementary failure no longer blanks a readable dial,
    // KEY-221). See startWorkspaceLoad for when the portfolio is asked for, and when it is waited on.
    const run = startWorkspaceLoad(api, token, id, {
      navOrgId,
      drillIn,
      knownOrgId: knownOrgId.current,
      portfolio: portfolioSeen.current,
    });

    // The summary (organisation and definition names) belongs to a dial and doesn't change, so it is kept
    // whenever it is for the dial in the URL — not only when its load is still the current one, or a realtime
    // reload starting while the portfolio is out would drop it for good.
    if (firstVisit && portfolioSeen.current) {
      setSummaryFor({ id, value: portfolioSeen.current.find((s) => s.id === id) ?? null });
    }
    void run.portfolio?.then((list) => {
      if (!list) {
        return;
      }
      portfolioSeen.current = list;
      if (sequencer.isLive(id)) {
        setSummaryFor({ id, value: list.find((s) => s.id === id) ?? null });
      }
    });
    // Cached only by the load that counts. This used to be written before the staleness check, so a late
    // answer for the dial just left could hand its org to the dial now open.
    void run.owningOrg.then((org) => {
      if (isCurrent()) {
        knownOrgId.current = org;
      }
    });
    // None of these is needed to draw the dial, so each lands as it arrives rather than holding the face back.
    // Customisation off still loads fine — the editor just stays hidden — and a failed subscriptions read
    // leaves the Subscribe controls as they were.
    void Promise.all([run.history, run.itemHistory]).then(([events, itemEvents]) => {
      if (isCurrent()) {
        setHistoryFor({ id, value: events });
        setItemHistoryFor({ id, value: itemEvents });
      }
    });
    void run.customisation.then((view) => {
      if (isCurrent()) {
        setCustFor({ id, value: view });
      }
    });
    void run.subscriptions.then((next) => {
      if (next && isCurrent()) {
        setSubsFor({ id, value: next });
      }
    });

    try {
      const next = await run.detail;
      if (!isCurrent()) {
        return; // a newer load — or another dial — superseded this one; its response wins
      }
      shownId.current = id;
      setShown({ id, nav: openedWith });
      setDetail(next);
      setError(null);
    } catch (e) {
      if (!isCurrent()) {
        return;
      }
      // A 404 means the dial is gone or no longer visible to us — deleted, reset away, or a stale
      // bookmark/link/notification pointing at an id that isn't in this environment. Don't blank the
      // workspace with a raw "…failed: 404" (the deny-by-default read also 404s, KEY-49); bounce to
      // My Dials with a notice instead — the same graceful degradation KEY-221 gave the history fetches.
      if (e instanceof ApiError && e.status === 404) {
        navigate("/instances", { replace: true, state: { dialUnavailable: true } });
        return;
      }
      // KEY-434: the dial is there and they are allowed it — it just has not been told something it was
      // set up to insist on. Show the capture screen rather than an error; a requirement is not a fault.
      if (e instanceof ApiError && e.status === 428 && e.requirements?.length) {
        setFirstRun(e.requirements);
        return;
      }
      setError(e instanceof Error ? e.message : "Failed to load the dial.");
    } finally {
      // KEY-498: the role swap is done when the payload it produced has landed — including the paths above
      // that return early, or the toggle would stay disabled after a 404 bounce.
      if (isCurrent()) {
        setSimulationBusy(false);
      }
    }
    // `requestedRole` is a dependency so that changing it reloads the whole dial under the new role. The
    // effect that pushes it onto the api-client is declared ABOVE this one, so the header is already set by
    // the time this fetch goes out.
  }, [id, navOrgId, drillIn, navigate, requestedRole, sequencer]);

  // KEY-795: a section's colour changes by itself when something on it falls due and counts as missed. The server
  // says when the next one is; read the dial again just after. Further off than a day, some other read comes first.
  const nextColourChange = detail?.nextColourChangeUtc;
  useEffect(() => {
    if (!nextColourChange) {
      return;
    }
    const wait = new Date(nextColourChange).getTime() - Date.now();
    if (!(wait > 0) || wait > 24 * 60 * 60 * 1000) {
      return;
    }
    const timer = window.setTimeout(() => void load(), wait + 1000);
    return () => window.clearTimeout(timer);
  }, [nextColourChange, load]);

  useEffect(() => {
    void load();
  }, [load]);

  // KEY-817: a dial of days is only as fresh as its calendars' last sync. Opening one asks the server to sync any
  // calendar on it that's more than a few minutes old (in the background — the read above doesn't wait), and if it
  // started anything, reads the dial again shortly after. Once per dial opened, not per reload.
  const daysDialId = detail && detail.id === id && detail.sections.some((s) => s.kind === "Days") ? detail.id : null;
  useEffect(() => {
    const token = getAccessToken();
    if (!daysDialId || !token) {
      return;
    }
    let live = true;
    let timer: number | null = null;
    api.refreshInstanceCalendars(token, daysDialId)
      .then((result) => {
        if (live && result.started > 0) {
          timer = window.setTimeout(() => void load(), CALENDAR_REFRESH_LOOK_AGAIN_MS);
        }
      })
      // Best-effort: the 15-minute sweep still runs, and Sync calendars is in the account menu.
      .catch(() => {});
    return () => {
      live = false;
      if (timer != null) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysDialId]);

  // A coalesced "refresh soon": one user action produces a burst of reload causes (the write's own
  // reconcile plus the realtime echo of that same write, sometimes twice), and each full reload is
  // several requests plus a whole-workspace re-render. The debounce folds a burst into one fetch; the
  // optimistic merges below mean nothing user-visible is waiting on it. GroupWorkspace does the same.
  const loadTimer = useRef<number | null>(null);
  const scheduleLoad = useCallback(() => {
    // A write on the last dial finishing after navigation must not displace this dial's pending reload.
    if (!sequencer.isLive(id)) {
      return;
    }
    if (loadTimer.current != null) {
      window.clearTimeout(loadTimer.current);
    }
    loadTimer.current = window.setTimeout(() => {
      loadTimer.current = null;
      void load();
    }, 600);
  }, [load, id, sequencer]);
  useEffect(() => () => {
    if (loadTimer.current != null) {
      window.clearTimeout(loadTimer.current);
    }
  }, []);

  // KEY-817: Sync calendars in the account menu has just fetched my calendars — read this dial's days again.
  useEffect(() => {
    const onSynced = () => scheduleLoad();
    window.addEventListener(CALENDARS_SYNCED_EVENT, onSynced);
    return () => window.removeEventListener(CALENDARS_SYNCED_EVENT, onSynced);
  }, [scheduleLoad]);

  // Live updates for this instance: reload on a pushed event and after a reconnect (backfill).
  // Coarse reload-on-any-event for now; the KEY-46 producer defines payloads for finer filtering.
  useRealtimeGroup(id ? groups.instance(id) : null, { onEvent: scheduleLoad, onReconnect: load });

  const vm = useMemo(() => (detail ? buildDialVM(detail) : null), [detail]);

  // Tell guided help which kind of dial is open, so "how do I change a status" offers the guide that
  // matches what's actually on screen (KEY-446). Cleared on the way out — off a dial we don't know.
  // Not while the next dial loads: what is on screen then is the last dial, and a guide would teach that one.
  const helpDialKind: HelpDialKind | null = vm && fresh ? (vm.simple ? "simple" : "itemised") : null;
  useEffect(() => {
    setDialKind(helpDialKind);
    return () => setDialKind(null);
  }, [helpDialKind, setDialKind]);

  // Org instance-managers act with full authority; otherwise a user's power comes from their per-instance
  // grants — and a user may hold several at different scopes (e.g. Editor on one section, Reader elsewhere).
  const realManagesInstances = !!me?.capabilities.includes("instance.manage");

  // KEY-498. Read from the PAYLOAD — what the server says it actually evaluated as — never from
  // `requestedRole`. A request the server declines then shows up as the toggle snapping back to "my own
  // role", instead of a UI that claims to be a Reader while the server keeps answering as an Admin.
  const simulatedRole = detail?.simulatedRole ?? null;
  const simulating = isSimulatableRole(simulatedRole);
  // The org capability IS the Admin standing being set aside, so it is suppressed for the duration —
  // exactly as the server suppresses it. Simulating Admin suppresses nothing, there being nothing to lower.
  const simulatingNonAdmin = simulating && simulatedRole !== "Admin";
  const managesInstances = simulatingNonAdmin ? false : realManagesInstances;

  // The roles the caller holds at a given scope: the union of every grant covering that section/segment
  // (managers act as Admin). Mirrors the server's effective-role aggregation, so the UI offers the right
  // status options per action point; the server is the real gate.
  const rolesAt = useCallback(
    (sectionKey: string, segmentKey: string, itemKey?: string): Set<string> => {
      const real = new Set<string>();
      if (detail && me) {
        for (const m of detail.members) {
          if (m.userId !== me.userId) continue;
          // KEY-852: an item-level grant counts at that item only — the server's ScopeMatch rule.
          if ((!m.sectionKey || m.sectionKey === sectionKey) && (!m.segmentKey || m.segmentKey === segmentKey)
            && (!m.itemKey || m.itemKey === itemKey)) {
            real.add(m.role);
          }
        }
      }
      // viewRoles applies the simulation, including setting the org capability aside — hence the REAL flag.
      return viewRoles(real, realManagesInstances, simulatedRole);
    },
    [detail, me, realManagesInstances, simulatedRole],
  );

  // KEY-918: the status's audience is the second half — who it is for, beside the roles that may set it.
  // "Responsible person only" reads the Responsible person field at the node, with inheritance.
  const responsibleCtx = useMemo(() => (detail ? responsibleContextOf(detail) : undefined), [detail]);
  const audienceViewer = useMemo(
    () => ({ userId: me?.userId ?? null, email: me?.email ?? null, isGuest: detail?.viewerIsGuest === true }),
    [me?.userId, me?.email, detail?.viewerIsGuest],
  );
  const canSet = useCallback(
    (status: GeneratedStatus, sectionKey: string, segmentKey: string, itemKey?: string) => {
      const roles = rolesAt(sectionKey, segmentKey, itemKey);
      const isAdmin = roles.has("Admin");
      if (!isAdmin && !status.allowedRoles.some((r) => roles.has(r))) return false;
      // KEY-916: while someone else has the node (or a node above it), only they or an admin move its status.
      if (!isAdmin && detail) {
        const held = coveringAssignment(detail, { sectionKey, segmentKey, itemKey });
        if (held && !isAssignee(held, audienceViewer, detail.contacts ?? [])) return false;
      }
      const responsible = status.audience === "Responsible"
        ? responsibleAt(responsibleCtx, { sectionKey, segmentKey, itemKey })
        : null;
      return audienceAllows(status, isAdmin, audienceViewer, responsible, detail?.contacts ?? []);
    },
    [rolesAt, responsibleCtx, audienceViewer, detail],
  );

  // KEY-916: who may hand nodes out here — Editor or Admin at the node — and who may take them back (a dial admin).
  const allocation = useMemo(
    () => ({
      canAllocate: (scope: { sectionKey: string; segmentKey?: string | null; itemKey?: string | null }) => {
        const roles = rolesAt(scope.sectionKey, scope.segmentKey ?? "", scope.itemKey ?? undefined);
        return roles.has("Admin") || roles.has("Editor");
      },
      isDialAdmin: rolesAt("", "").has("Admin"),
    }),
    [rolesAt],
  );

  // What the guided-help tours may teach this person on this dial (KEY-535). Computed here because this is
  // the only place that holds all three answers, and pushed into the controller rather than read by it.
  //
  // `setStatus` is the one worth explaining: StatusPicker always RENDERS, with the statuses you may not set
  // greyed out rather than hidden, so a tour's anchor resolves for a Reader exactly as it does for an Editor
  // and would spotlight a control to describe a gesture they cannot perform. Asking whether there is
  // anywhere at all they could set one — through the same `canSet` the panels use, simulation included — is
  // the honest question. A confined session (kiosk PIN or guest) loses the header's Feedback button outright.
  const helpAudience = useMemo(
    () => (vm && fresh && detail
      ? {
          confined: kioskNav(me).locked,
          canSubscribe: capabilities.subscriptions,
          canSetStatus: canSetAnyStatus(vm, canSet),
          hasCalendars: detail.sections.some((s) => s.kind === "Days"),
        }
      : null),
    [vm, fresh, detail, me, capabilities.subscriptions, canSet],
  );
  useEffect(() => {
    setHelpAudience(helpAudience);
    return () => setHelpAudience(null);
  }, [helpAudience, setHelpAudience]);

  // KEY-814: the dial's welcome page. The server only sends it to the editors and admins it is written for, and says
  // when to show it unasked (first open, or a new version changed it). `welcomeOpenFor` holds the dial whose page is
  // up, so a page can never be left open over the next dial; `welcomeClosed` stops a re-offer before the next read
  // has caught up with the close.
  const [welcomeOpenFor, setWelcomeOpenFor] = useState<string | null>(null);
  /** KEY-820: "Add" on a day, asking whether it's an appointment in my calendar or just on this dial. `resolve` hands the
   * new item's key back to the panel that asked, which opens it. */
  const [addOnDay, setAddOnDay] = useState<{
    segmentKey: string;
    sectionKey?: string;
    dayLabel: string;
    sources: InstanceCalendarSourceView[];
    resolve: (key: string | null) => void;
  } | null>(null);
  /** KEY-821: one of my own appointments being changed from its card. */
  const [changing, setChanging] = useState<{ segmentKey: string; appointment: AppointmentBeingChanged } | null>(null);
  const welcomeClosed = useRef(new Set<string>());
  const [welcomeTab, setWelcomeTab] = useState<WorkspaceTab | undefined>(undefined);
  const welcome = fresh && detail && welcomeHasContent(detail.welcome) ? detail.welcome! : null;
  useEffect(() => {
    if (fresh && detail?.offerWelcome && welcomeHasContent(detail.welcome) && !welcomeClosed.current.has(detail.id)) {
      setWelcomeOpenFor(detail.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh, detail?.id, detail?.offerWelcome]);
  const welcomeDialName = detail ? (forDial(summaryFor, detail.id, null)?.definitionName ?? detail.definitionName ?? detail.name) : "";
  useEffect(() => {
    if (!welcome || !detail) {
      return;
    }
    const dialId = detail.id;
    setWelcomeOpener({ open: () => setWelcomeOpenFor(dialId), dialName: welcomeDialName });
    return () => setWelcomeOpener(null);
  }, [welcome, detail?.id, welcomeDialName]);
  // The tab a welcome button asked for reaches DialWorkspace as a CHANGE of initialPanelTab; clearing it straight
  // after lets the same button work again later (the workspace only reacts to a change, never to the value).
  useEffect(() => {
    if (welcomeTab) setWelcomeTab(undefined);
  }, [welcomeTab]);
  const closeWelcome = () => {
    const dialId = welcomeOpenFor;
    setWelcomeOpenFor(null);
    if (!dialId) {
      return;
    }
    welcomeClosed.current.add(dialId);
    const token = getAccessToken();
    // Best effort: if it fails, the page simply offers itself once more next time.
    if (token) void api.markWelcomeSeen(token, dialId).catch(() => {});
  };

  // Membership-derived gates. isMember = holds any grant (doc edit / discussion). isDialAdmin = an
  // instance-wide Admin grant or an org instance-manager (member management, centre logo, and deleting
  // discussion messages — KEY-582). Only org instance-managers (managesInstances) may grant the Admin tier.
  const isMember = managesInstances || (!!me && !!detail && detail.members.some((m) => m.userId === me.userId));
  const holdsInstanceWideAdmin =
    !!me && !!detail && detail.members.some((m) => m.userId === me.userId && !m.sectionKey && !m.segmentKey && m.role === "Admin");
  // Mirrors the server's HoldsInstanceWideAdminAsync exactly, simulation and all: a checker looking as a
  // Reader is not a dial admin, however many Admin rows checking seeded for them.
  const isDialAdmin = simulatingNonAdmin ? false : realManagesInstances || holdsInstanceWideAdmin;

  // KEY-298: the dial's action matrix decides who may do what beyond statuses, exactly as allowedRoles
  // decides statuses. The union of the caller's grants mirrors the server's stance for instance-wide
  // actions; the server still enforces per-scope precisely on every write. Older payloads without a
  // matrix fall back to the historical assumptions.
  const myUnionRoles = (() => {
    const roles = new Set<string>();
    if (me && detail) {
      for (const m of detail.members) {
        if (m.userId === me.userId) roles.add(m.role);
      }
    }
    return viewRoles(roles, realManagesInstances, simulatedRole);
  })();
  const mayDo = (allowed: string[] | undefined) =>
    !!allowed && (myUnionRoles.has("Admin") || allowed.some((r) => myUnionRoles.has(r)));
  const actions = detail?.roleActions;
  const canEditDocs = actions ? mayDo(actions.documentUpload) : isMember;
  const canEditAttributes = actions ? mayDo(actions.attributeEdit) : isMember;
  const canManageDocVersions = actions ? mayDo(actions.documentVersions) : isDialAdmin;
  const canPost = actions ? mayDo(actions.messagePost) : isMember;
  // The two READ gates (KEY-537). `?? undefined` rather than `?? []`: a null mask means the SERVER didn't
  // say — an older payload — and mayDo(undefined) is false, so the fallback after it is what applies.
  // That fallback is `true`, not `isMember`, because before these masks existed anyone who could see the
  // dial could see its files, and defaulting narrower would blank the Files tab for a whole population the
  // server would happily serve. The server is the real gate either way.
  const canListDocs = actions?.documentList ? mayDo(actions.documentList) : true;
  const canDownloadDocs = actions?.documentDownload ? mayDo(actions.documentDownload) : true;

  // Restricted messaging (KEY-285). The roster the picker draws on is already in the detail payload —
  // every member gets it — so there is no extra endpoint here; it is narrowed to people the dial lets
  // this sender ADDRESS (the action's second mask), with archived accounts and the sender dropped. The
  // thread narrows it again to whoever can read the action point in question. All of this is a hint: the
  // server re-checks every recipient and refuses the whole post if one of them doesn't qualify.
  const restricted = (() => {
    if (!detail) {
      return { canPost: false, recipients: [], mentionable: [] };
    }
    const canPostRestricted = mayDo(actions?.messagePostRestricted ?? undefined);
    const targetMask = actions?.messagePostRestrictedTo ?? null;
    const byUser = new Map<string, { userId: string; name: string; role: string; roles: Set<string>; scopes: { sectionKey: string | null; segmentKey: string | null; itemKey?: string | null }[] }>();
    for (const m of detail.members) {
      if (m.archived || m.userId === me?.userId) continue;
      const entry = byUser.get(m.userId) ?? {
        userId: m.userId,
        name: m.displayName?.trim() || m.email,
        role: m.role,
        roles: new Set<string>(),
        scopes: [],
      };
      entry.roles.add(m.role);
      entry.scopes.push({ sectionKey: m.sectionKey ?? null, segmentKey: m.segmentKey ?? null, itemKey: m.itemKey ?? null });
      byUser.set(m.userId, entry);
    }
    const people = [...byUser.values()].sort((a, b) => a.name.localeCompare(b.name));
    const toOption = (e: (typeof people)[number]) => ({ userId: e.userId, name: e.name, role: [...e.roles].join(", "), scopes: e.scopes });
    // KEY-592: anyone on the dial can be @mentioned — a mention notifies rather than addresses, so the
    // restricted-messaging permission and target mask below don't narrow it. The thread narrows it to who
    // can see the action point.
    const mentionable = people.map(toOption);
    if (!canPostRestricted) {
      return { canPost: false, recipients: [], mentionable };
    }
    const recipients = people
      .filter((e) => !targetMask || e.roles.has("Admin") || targetMask.some((r) => e.roles.has(r)))
      .map(toOption);
    return { canPost: true, recipients, mentionable };
  })();

  // KEY-299: per-attribute access, same union-role stance as the flags above. The park-and-reveal gate
  // flow refuses to park a pick whose required field fails either half; the server enforces exactly and
  // prunes restricted values before they ever reach this client.
  const roleAllows = (roles: string[] | null | undefined) =>
    !roles || myUnionRoles.has("Admin") || roles.some((r) => myUnionRoles.has(r));
  // An explicit per-person override (KEY-299 fine-tune) wins over the role rules in both directions,
  // exactly as the server enforces; admins are never overridden.
  const myOverrides = new Map(
    (detail?.myAttributeOverrides ?? []).map((o) => [`${o.target}:${o.attributeKey}`, o.access]),
  );
  const attributeAccess = (decl: GeneratedNodeAttribute) => {
    const overridden = myOverrides.get(`${decl.target}:${decl.key}`);
    if (overridden && !myUnionRoles.has("Admin")) {
      const see = overridden !== "Hidden";
      return { see, edit: see && overridden === "Edit" };
    }
    const see = roleAllows(decl.visibleToRoles);
    return { see, edit: see && canEditAttributes && roleAllows(decl.editableByRoles) };
  };

  // KEY-239: when structure editing is gated off we used to render nothing at all, so a dial admin
  // looking for "Edit structure" (the only way in to item import) had no way to tell whether the feature
  // was off, mis-configured, or broken. Explain it instead — using the server's reason, which is the
  // actual gate; isDialAdmin here only decides *who* is shown the note (ordinary members still see
  // nothing), and is deliberately the stricter client-side notion of dial admin.
  const structureNotice = useMemo(() => {
    if (!cust || cust.canCustomise || !isDialAdmin) return null;
    switch (cust.blockReason) {
      case "VersionEnvelopeOff":
        // The one version-dependent gate, and the one that produces "but I upgraded": an instance stays
        // pinned to its current version until an accepted upgrade actually applies.
        return detail?.version
          ? `This dial is on version ${detail.version}, which doesn't have structure editing switched on. An accepted update only takes effect once it's applied.`
          : "The version this dial is on doesn't have structure editing switched on.";
      case "DefinitionMasterSwitchOff":
      case "GrantBlocked":
        return "Structure editing isn't switched on for this dial. Ask your Key Dials administrator to enable it.";
      case "NotPermitted":
        return "You don't have permission to edit this dial's structure.";
      default:
        return null;
    }
  }, [cust, isDialAdmin, detail?.version]);

  // Most-recent activity across status + item history; falls back to created (no dial-level updatedAt exists).
  const lastUpdated = useMemo(() => {
    const times = [...history, ...itemHistory].map((e) => e.changedAtUtc).filter(Boolean);
    return times.length ? times.reduce((a, b) => (a > b ? a : b)) : summary?.createdAtUtc ?? null;
  }, [history, itemHistory, summary]);

  // Kiosk auto-lock (KEY-268): on a shared device this dial drops to a lock screen after a stretch with
  // nobody touching it. The effective timeout arrives resolved from the server, so nothing here has to know
  // the definition/instance inheritance. Deliberately declared above the loading/error early-returns —
  // hooks can't be conditional — and inert until the dial actually says it locks.
  const autoLock = detail?.autoLock ?? null;
  const centralImageUrl = detail?.centralImageUrl ?? null;
  const onIdle = useCallback(() => {
    if (id) {
      // The centre image rides along so the lock screen can render it without a request: a lock ticket is
      // refused every data route by design.
      void lockTo(id, centralImageUrl);
    }
  }, [id, centralImageUrl, lockTo]);
  useIdleLock({
    // Never idle-lock a guest session (KEY-297): a guest can't PIN-unlock — they're excluded from the
    // candidates by design — so locking would strand the screen and revoke their refresh chain for
    // nothing. The server refuses a guest /api/auth/lock as well; this just avoids attempting it.
    // Held off while the next dial loads: `autoLock` is still the last dial's, and the lock would name this one.
    enabled: fresh && !!autoLock?.enabled && !guestInstanceId,
    timeoutSeconds: autoLock?.timeoutSeconds ?? 0,
    onIdle,
  });

  // Inline item add/remove rides the customisation overlay: instance admins (canCustomise), when the
  // definition allows it. Add respects allowAddItems + the per-segment cap; hiding a base item needs
  // allowDisableBaseNodes. The server re-checks all of this — these flags just gate the controls.
  const canAddItems = !!cust?.canCustomise && !!cust.effectivePolicy.allowAddItems;
  const canRemoveCustomItems = !!cust?.canCustomise;
  const canHideBaseItems = !!cust?.canCustomise && !!cust.effectivePolicy.allowDisableBaseNodes;
  // Renaming mirrors the structure editor: custom items are always renamable; base (definition) items
  // only when the dial's policy allows wording edits.
  const canRenameCustomItems = !!cust?.canCustomise;
  const canRenameBaseItems = !!cust?.canCustomise && !!cust.effectivePolicy.allowEditWording;
  const maxItemsPerSegment = cust?.headroom.maxItemsPerSegment ?? null;

  // Every write below acts for the dial its closure was rendered for, and only while that dial is both the one
  // in the URL and the one whose detail is on screen. While the next dial loads its controls are inert anyway;
  // this also stops a write begun on the last dial from reverting, merging or reporting an error into the next
  // one when its response arrives after navigation, and stops anything that calls in without a click (the
  // KEY-248 parked-pick auto-commit) from sending one dial's section and segment keys to another dial's id.
  const onScreen = (forId: string | undefined): forId is string =>
    !!forId && sequencer.isLive(forId) && shownId.current === forId;

  // Status changes flip locally the moment the request is sent, and a refusal reverts them with the
  // error banner: the pill answers immediately instead of after a full reload. The debounced reconcile
  // brings in everything derived server-side (rollups, bands, audit trail, link propagation).
  async function applySegmentStatus(sectionKey: string, segmentKey: string, statusKey: string, review?: ReviewScheduleInput) {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return;
    }
    const prior = detail ? findSegmentStatus(detail, { sectionKey, segmentKey }) : null;
    setDetail((prev) => (prev ? mergeSegmentStatus(prev, { sectionKey, segmentKey, statusKey }) : prev));
    try {
      // A status that REQUIRES a review date (KEY-412) arrives with one — unless the node already carries a
      // date, which the server then keeps. Every other pick clears a one-off schedule, exactly as it always did.
      // A status flagged "Needs a comment" (KEY-919) arrives with one; the server refuses it blank.
      await api.changeSegmentStatus(token, id, {
        sectionKey, segmentKey, statusKey, note: review?.comment?.trim() || null,
        reviewDate: review?.reviewDate ?? null,
        revertToStatusKey: review?.revertToStatusKey ?? null,
        handOnTo: review?.handOnTo ?? null,
      });
      scheduleLoad();
    } catch (e) {
      if (!onScreen(id)) {
        return;
      }
      setDetail((prev) =>
        prev ? (prior ? mergeSegmentStatus(prev, prior) : removeSegmentStatus(prev, { sectionKey, segmentKey })) : prev);
      setError(statusChangeMessage(e));
    }
  }

  // KEY-523: the two Display switches are ONE boolean on the instance each — the server writes the flag
  // and nothing else. They used to hand back `load`, which refetched the instance detail, the history, the
  // item history, the customisation and the subscriptions: five requests, the first of which is the whole
  // group rollup, with the checkbox held disabled for the round trip. On a group that is the slowest
  // request in the app, spent to learn a value we already knew because we just sent it.
  //
  // Merged locally instead, in the optimistic shape the status writes below already use: the dial answers
  // at once and a refusal puts the old value back. Nothing else in the payload depends on either flag —
  // `sectionEdgeSegmentsOf` recomputes the split from data already loaded, and hide-empty is applied in
  // DialWorkspace from this same field.
  //
  // KEY-547 adds `hideEmptyForMe` to the same merge: enabling the setting now also turns the filter on for
  // the person who enabled it, and the server withholds that field while the instance flag is off — so
  // without merging it a user with an existing preference row would see the dial's own switch read "off"
  // until the next reload. The merge feeds DialWorkspace, which syncs the switch from this field.
  function applyDisplaySetting(patch: Partial<Pick<InstanceDetail, "hideEmptyEnabled" | "hideEmptyForMe" | "sectionEdgeSplitEnabled" | "miloHidden">>) {
    if (!onScreen(id)) {
      return;
    }
    setDetail((prev) => (prev ? { ...prev, ...patch } : prev));
  }

  // A whole section's status (KEY-479): set, or cleared with null. Same optimistic shape as an action
  // point — the edge on the dial answers at once, and a refusal puts the old row back with the banner.
  async function applySectionStatus(sectionKey: string, statusKey: string | null) {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return;
    }
    const prior = detail?.sectionStatuses?.find((s) => s.sectionKey === sectionKey)?.statusKey ?? null;
    setDetail((prev) => (prev ? mergeSectionStatus(prev, { sectionKey, statusKey }) : prev));
    try {
      await api.changeSectionStatus(token, id, { sectionKey, statusKey, note: null });
      scheduleLoad();
    } catch (e) {
      if (!onScreen(id)) {
        return;
      }
      setDetail((prev) => (prev ? mergeSectionStatus(prev, { sectionKey, statusKey: prior }) : prev));
      setError(statusChangeMessage(e));
    }
  }

  // Set (or clear) a node-attribute value (KEY-161). Once the server accepts it, the value merges into
  // the local detail rather than waiting out a reload — the KEY-248 parked-pick auto-commit watches
  // these values, so this is what lets a gated status apply after ONE round trip instead of seven. The
  // merge deliberately happens only AFTER the write succeeds: the server re-checks the gate on the
  // status write, so the attribute must be committed before the auto-commit fires the status change.
  async function applyNodeAttribute(request: SetNodeAttributeRequest) {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return;
    }
    try {
      await api.setNodeAttribute(token, id, request);
      if (!onScreen(id)) {
        return;
      }
      setDetail((prev) => (prev ? mergeNodeAttributeValue(prev, request) : prev));
      scheduleLoad();
    } catch (e) {
      if (onScreen(id)) {
        setError(e instanceof ApiError && e.status === 403 ? "You don't have permission to edit that field." : "Could not save the field.");
      }
    }
  }

  // Schedule (or clear) an auto-revert on a segment: re-applies its current status with the chosen review
  // date + revert target (Phase 6, KEY-51). The segment's current status comes from the view model.
  async function scheduleSegmentReview(
    sectionKey: string,
    segmentKey: string,
    reviewDate: string | null,
    revertToStatusKey: string | null,
    reviewReminderLeadDays: number | null,
    reviewReminderRepeatDays: number | null,
    reviewRecurrenceUnit: string | null,
    reviewRecurrenceCount: number | null,
  ) {
    const token = getAccessToken();
    const statusKey = vm?.sections.find((s) => s.key === sectionKey)?.segments.find((g) => g.key === segmentKey)?.statusKey;
    if (!token || !onScreen(id) || !statusKey) {
      return;
    }
    try {
      await api.changeSegmentStatus(token, id, {
        sectionKey, segmentKey, statusKey, reviewDate, revertToStatusKey, note: null,
        reviewReminderLeadDays, reviewReminderRepeatDays, reviewRecurrenceUnit, reviewRecurrenceCount,
      });
      await load();
    } catch (e) {
      if (!onScreen(id)) {
        return;
      }
      setError(
        e instanceof ApiError && e.status === 400
          ? "A review date is required to schedule an auto-revert."
          : "Could not update the review date.",
      );
    }
  }

  // KEY-376: an item's review date, the itemised counterpart to scheduleSegmentReview. Sent as a status
  // change carrying the status the item is ALREADY on, because the schedule lives on the status row —
  // the same shape (and the same endpoint) as scheduling a review on an action point.
  async function scheduleItemReview(
    sectionKey: string,
    segmentKey: string,
    itemKey: string,
    dimensionKey: string,
    reviewDate: string | null,
    revertToStatusKey: string | null,
    reviewReminderLeadDays: number | null,
    reviewReminderRepeatDays: number | null,
    reviewRecurrenceUnit: string | null,
    reviewRecurrenceCount: number | null,
  ) {
    const token = getAccessToken();
    const key = { sectionKey, segmentKey, itemKey, dimensionKey };
    const statusKey = detail ? findItemStatus(detail, key)?.statusKey : undefined;
    if (!token || !onScreen(id) || !statusKey) {
      return;
    }
    try {
      await api.changeItemStatus(token, id, {
        ...key, statusKey, note: null, reviewDate, revertToStatusKey,
        reviewReminderLeadDays, reviewReminderRepeatDays, reviewRecurrenceUnit, reviewRecurrenceCount,
      });
      await load();
    } catch (e) {
      if (!onScreen(id)) {
        return;
      }
      setError(
        e instanceof ApiError && e.status === 400
          ? "A review date is required to schedule an auto-revert."
          : "Could not update the review date.",
      );
    }
  }

  async function applyItemStatus(
    sectionKey: string,
    segmentKey: string,
    itemKey: string,
    dimensionKey: string,
    statusKey: string,
    review?: ReviewScheduleInput,
  ) {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return;
    }
    // Optimistic, like applySegmentStatus above: flip now, revert (to the exact prior row, or to unset)
    // if the server refuses — e.g. a gate this client's mirror disagreed on.
    const key = { sectionKey, segmentKey, itemKey, dimensionKey };
    const prior = detail ? findItemStatus(detail, key) : null;
    setDetail((prev) => (prev ? mergeItemStatus(prev, { ...key, statusKey }) : prev));
    try {
      await api.changeItemStatus(token, id, {
        // KEY-919: the comment a "Needs a comment" status asks for travels as the note.
        sectionKey, segmentKey, itemKey, dimensionKey, statusKey, note: review?.comment?.trim() || null,
        reviewDate: review?.reviewDate ?? null,
        revertToStatusKey: review?.revertToStatusKey ?? null,
        handOnTo: review?.handOnTo ?? null,
      });
      scheduleLoad();
    } catch (e) {
      if (!onScreen(id)) {
        return;
      }
      setDetail((prev) => (prev ? (prior ? mergeItemStatus(prev, prior) : removeItemStatus(prev, key)) : prev));
      setError(statusChangeMessage(e));
    }
  }

  // KEY-753: what the section holding a segment, or an item, calls its items — "appointment" on a family dial's
  // Appointments section, so a new one is "New appointment" and its errors say "appointment" too.
  function itemWordsForSegment(segmentKey: string, sectionKey?: string) {
    return itemNouns(detail, sectionKey ?? detail?.sections.find((s) => s.segments.some((g) => g.key === segmentKey))?.key);
  }
  function itemWordsForItem(itemKey: string) {
    return itemNouns(detail, detail?.sections.find((s) => s.segments.some((g) => g.items.some((i) => i.key === itemKey)))?.key);
  }

  // KEY-820: on a day, someone whose calendar here takes appointments is asked which they mean first. Anyone else, and
  // anywhere else, adds an item straight away as before.
  async function addItem(segmentKey: string, sectionKey?: string): Promise<string | null> {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return null;
    }
    if (isDaySegmentKey(segmentKey)) {
      let sources: InstanceCalendarSourceView[] = [];
      try {
        sources = appointmentSources((await api.instanceCalendarSources(token, id)).sources);
      } catch {
        // Can't tell: add to the dial as before.
      }
      if (sources.length > 0 && onScreen(id)) {
        const section = detail?.sections.find((s) => s.segments.some((g) => g.key === segmentKey));
        const segment = section?.segments.find((g) => g.key === segmentKey);
        const dayLabel = [section?.label, segment && segment.name !== section?.label ? segment.name.toLowerCase() : null].filter(Boolean).join(" ");
        return new Promise((resolve) => setAddOnDay({ segmentKey, sectionKey, dayLabel: dayLabel || "this day", sources, resolve }));
      }
    }
    return addDialItem(segmentKey, sectionKey);
  }

  // KEY-821: delete one of my own appointments from my calendar, then show the dial without it.
  async function deleteAppointment(itemKey: string) {
    const token = getAccessToken();
    if (!token || !onScreen(id)) return;
    try {
      await api.deleteCalendarAppointment(token, id, itemKey);
      await load();
    } catch (e) {
      if (!onScreen(id)) return;
      setError(e instanceof ApiError && e.message ? e.message : "The appointment couldn't be deleted. It's still in your calendar.");
    }
  }

  // Add a custom item to the selected action point (instance customisation overlay), refresh, and
  // return the new item's key so the caller can expand it.
  async function addDialItem(segmentKey: string, sectionKey?: string): Promise<string | null> {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return null;
    }
    const words = itemWordsForSegment(segmentKey, sectionKey);
    try {
      const { key } = await api.addInstanceItem(token, id, { parentKey: segmentKey, name: `New ${words.one}` });
      await load();
      return key;
    } catch (e) {
      if (!onScreen(id)) {
        return null;
      }
      setError(
        e instanceof ApiError && e.status === 403
          ? `You don't have permission to add ${words.many} to this dial.`
          : e instanceof ApiError && e.status === 400
            ? `Could not add the ${words.one} — the ${words.one} limit for this ${noun.one} may have been reached.`
            : `Could not add the ${words.one}.`,
      );
      return null;
    }
  }

  // Add an upload tag to this dial (KEY-499). Returns an error message for the picker to show inline
  // beside the name just typed, or null on success — the refusals are all about the label itself
  // (reserved, duplicate, too long, at the cap) and the server words them, so they are passed straight
  // through rather than second-guessed here.
  //
  // Reloads the whole detail rather than patching a local list: every Files pane on screen reads its
  // options from detail.documentTags, so one reload updates the action point's picker and every open item
  // card's at once.
  async function createDocumentTag(label: string): Promise<string | null> {
    const token = getAccessToken();
    if (!token || !id) {
      return "You are not signed in.";
    }
    if (!onScreen(id)) {
      return "Could not add the tag.";
    }
    try {
      await api.createDocumentTag(token, id, { label });
      await load();
      return null;
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        return "You don't have permission to add tags to this dial.";
      }
      if (e instanceof ApiError && e.status === 400) {
        return e.message || "That tag name can't be used.";
      }
      return "Could not add the tag.";
    }
  }

  // Remove an item from this dial: hard-delete a pristine custom item, or hide it reversibly (a
  // custom item that has activity, or a base/definition item). The panel decides which mode applies.
  async function removeItem(itemKey: string, mode: "delete" | "hide") {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return;
    }
    const words = itemWordsForItem(itemKey);
    try {
      if (mode === "delete") {
        await api.deleteInstanceNode(token, id, itemKey);
      } else {
        await api.patchInstanceNode(token, id, itemKey, { nodeType: "Item", disabled: true });
      }
      await load();
    } catch (e) {
      if (!onScreen(id)) {
        return;
      }
      setError(
        e instanceof ApiError && e.status === 403
          ? `You don't have permission to change ${words.many} on this dial.`
          : e instanceof ApiError && e.status === 409
            ? `This ${words.one} has activity, so it can only be hidden. Try again to hide it.`
            : mode === "delete"
              ? `Could not delete the ${words.one}.`
              : `Could not hide the ${words.one}.`,
      );
    }
  }

  // Rename an item via the customisation overlay, then refresh. Custom items patch by key alone; base
  // (definition) items must carry their nodeType (mirrors EditStructurePanel.saveField). The server
  // re-checks permission and rejects empty names.
  async function renameItem(itemKey: string, name: string, isCustom: boolean) {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return;
    }
    const next = name.trim();
    if (!next) {
      return;
    }
    const words = itemWordsForItem(itemKey);
    try {
      await api.patchInstanceNode(token, id, itemKey, isCustom ? { name: next } : { nodeType: "Item", name: next });
      await load();
    } catch (e) {
      if (!onScreen(id)) {
        return;
      }
      setError(
        e instanceof ApiError && e.status === 403
          ? `You don't have permission to rename ${words.many} on this dial.`
          : `Could not rename the ${words.one}.`,
      );
    }
  }

  // KEY-859: when something added to a day happens — all day, or from/until. Sent on its own, so the wording stays.
  async function setItemSpan(itemKey: string, span: ItemSpan | null) {
    const token = getAccessToken();
    if (!token || !onScreen(id)) return;
    try {
      await api.patchInstanceNode(token, id, itemKey, { span: span ?? {} });
      await load();
    } catch (e) {
      if (!onScreen(id)) return;
      setError(e instanceof ApiError && e.status === 400 && e.message ? e.message : "The time couldn't be saved.");
    }
  }

  // Set the auto-set-status-on-upload overlay (on/off + target-status override) for a whole action point
  // (itemKey null) or one item, then refresh.
  async function setUploadAutomation(sectionKey: string, segmentKey: string, itemKey: string | null,
    dimensionKey: string | null, enabled: boolean, statusKey: string | null) {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return;
    }
    try {
      await api.setUploadAutomation(token, id, { sectionKey, segmentKey, itemKey, dimensionKey, enabled, statusKey });
      await load();
    } catch (e) {
      if (!onScreen(id)) {
        return;
      }
      setError(
        e instanceof ApiError && e.status === 403
          ? "You don't have permission to change this setting."
          : e instanceof ApiError && e.status === 400
            ? "Choose the status this upload should set."
            : "Could not update the upload-status setting.",
      );
    }
  }

  // Change the dial's centre logo (a dial admin action). Rethrows a friendly error so the workspace's logo
  // modal can surface it; reloads the detail on success so the new (or reverted) image shows immediately.
  async function setCentralLogo(file: File) {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return;
    }
    try {
      await api.uploadInstanceCentralImage(token, id, file);
    } catch (e) {
      throw new Error(centreLogoUploadError(e));
    }
    await load();
  }

  async function clearCentralLogo() {
    const token = getAccessToken();
    if (!token || !onScreen(id)) {
      return;
    }
    try {
      await api.clearInstanceCentralImage(token, id);
    } catch {
      throw new Error("Could not revert the logo.");
    }
    await load();
  }

  // A first-run requirement or a failure for the dial in the URL takes the page, even while the last dial is
  // still being held on screen for it.
  if (firstRun && (!detail || stale)) {
    return (
      <FirstRunCapture
        instanceId={id!}
        dialName={forDial(summaryFor, id, null)?.name}
        requirements={firstRun}
        onDone={() => {
          // Clear it and re-run the load. If the server is still not satisfied it will 428 again with
          // whatever is left, which is the honest answer — nothing here decides the dial is open.
          setFirstRun(null);
          void load();
        }}
        onBack={() => navigate("/instances")}
      />
    );
  }
  if (error && (!detail || stale)) {
    return <div style={{ maxWidth: 720, margin: "0 auto", padding: 28, color: "var(--st-overdue)", fontSize: 14 }}>{error}</div>;
  }
  if (!detail || !vm) {
    return <div style={{ maxWidth: 720, margin: "0 auto", padding: 28, color: "var(--text-muted)", fontSize: 14 }}>Loading…</div>;
  }

  // The dial on screen: the one in the URL or, while the next one loads, the last one. Everything below renders
  // for it — its own id for the panels that fetch by id, its own breadcrumb — until the new detail replaces it.
  const viewId = shown?.id ?? id!;
  const crumbNav = stale ? shown?.nav ?? null : navState;
  const fromGroup = crumbNav?.groupId ? { id: crumbNav.groupId, name: crumbNav.groupName ?? "Group" } : null;
  const fromParent = crumbNav?.fromParentInstanceId
    ? { id: crumbNav.fromParentInstanceId, name: crumbNav.fromParentName ?? "Group dial" }
    : null;

  const itemEditPolicy: ItemEditPolicy | undefined = cust?.canCustomise
    ? {
        canAdd: canAddItems,
        canRemoveCustom: canRemoveCustomItems,
        canHideBase: canHideBaseItems,
        canRenameCustom: canRenameCustomItems,
        canRenameBase: canRenameBaseItems,
        maxItemsPerSegment,
      }
    : undefined;

  const controller: DialWorkspaceController = {
    // Look back's Bring back writes through the API itself, then asks for this: one reload once the sequence is over.
    onRefresh: () => void load(),
    onSetSegmentStatus: applySegmentStatus,
    onSetSectionStatus: applySectionStatus,
    onSetItemStatus: applyItemStatus,
    onScheduleRevert: scheduleSegmentReview,
    onScheduleItemRevert: (sectionKey, segmentKey, itemKey, dimensionKey, reviewDate, revertToStatusKey, leadDays, repeatDays, recurUnit, recurCount) =>
      void scheduleItemReview(sectionKey, segmentKey, itemKey, dimensionKey, reviewDate, revertToStatusKey, leadDays, repeatDays, recurUnit, recurCount),
    onSetNodeAttribute: applyNodeAttribute,
    onSubsChanged: reloadSubs,
    onAddItem: addItem,
    // Offered only to people who may upload here — naming a bucket is part of filing a file, and the
    // server enforces the same bar. Undefined for everyone else, which hides the "+ Add a tag" pill
    // rather than showing a control that would 403.
    onCreateDocumentTag: canEditDocs ? createDocumentTag : undefined,
    onRemoveItem: (itemKey, mode) => void removeItem(itemKey, mode),
    onRenameItem: (itemKey, name, isCustom) => void renameItem(itemKey, name, isCustom),
    onSetItemSpan: (itemKey, span) => void setItemSpan(itemKey, span),
    // KEY-821: only ever reached for an event whose card the server marked as the viewer's to change.
    onEditAppointment: (itemKey) => {
      const segment = detail?.sections.flatMap((s) => s.segments).find((g) => g.items.some((i) => i.key === itemKey));
      const item = segment?.items.find((i) => i.key === itemKey);
      const appointment = item ? appointmentBeingChanged(item, detail?.timeZoneId) : null;
      if (segment && appointment) setChanging({ segmentKey: segment.key, appointment });
    },
    onDeleteAppointment: (itemKey) => void deleteAppointment(itemKey),
    onSetUploadAutomation: (sectionKey, segmentKey, itemKey, dimensionKey, enabled, statusKey) =>
      void setUploadAutomation(sectionKey, segmentKey, itemKey, dimensionKey, enabled, statusKey),
    onDrillToChild: (childInstanceId, target) => navigate(`/instances/${childInstanceId}`, {
      state: {
        organisationId: navOrgId ?? undefined,
        fromParentInstanceId: viewId,
        fromParentName: detail?.name,
        focusSectionKey: target?.sectionKey ?? undefined,
        focusSegmentKey: target?.segmentKey ?? undefined,
      },
    }),
    onBackToList: () => navigate("/instances"),
    onSetCentralImage: setCentralLogo,
    onClearCentralImage: clearCentralLogo,
  };

  // KEY-453: SMS alerts silently deliver nothing without a phone number, so the channel is offered only to
  // users who have one. Now that a person can set their own number on the Settings page, this is a real
  // answer rather than a hardcoded false — before it, SMS was unreachable for everybody.
  const meHasPhoneNumber = !!me?.phoneNumber;

  // A dial admin (instance Admin or org instance-manager) may change the centre logo when the definition's
  // rebranding switch and the organisation logo policy in force allow it (KEY-718). The server re-checks on write.
  // KEY-756: isDialAdmin reads home-organisation capabilities, so on a dial in another organisation it can say yes
  // where the upload answers 403. The payload's canEditCentreLogo is the role in the dial's own organisation.
  const canEditCentreLogo = isDialAdmin && !!detail.logoOverrideAllowed && detail.canEditCentreLogo !== false;
  // KEY-640/718: an admin who can't is told why when they hover the centre, and who to ask — which depends on
  // whether it is this dial's switch, an organisation's managers-only policy, or a lock above.
  const centreLogoNote = centreLogoNoteFor(detail, isDialAdmin);

  // KEY-455. The menu is derived, not stored: a Reader gets an empty list, which is what hides the
  // Settings button for them rather than handing them a button onto an empty page.
  const settingsItems = dialSettingsItems({
    // Everyone who can open the dial may see who else is on it — the button this replaced was ungated.
    canSeeMembers: true,
    isDialAdmin,
    hasAutoLock: autoLock !== null,
    insightsEnabled,
    peopleEnabled,
    hasDaySections: detail.sections.some((s) => s.kind === "Days"),
    canEditStructure: !!cust?.canCustomise,
    hasStructureNotice: structureNotice !== null,
    // KEY-497. Matches the seat endpoint's own authorisation — a dial Admin OR an org instance-manager —
    // so the menu offers Licensing to exactly the people the API will answer for.
    managesInstances,
    // KEY-528: what the DIAL is, not what this caller may do — the one gate here that asks that.
    isGroup: !!detail.isGroup,
    // KEY-556: changes hints only. The server has already applied checking simulation to `allowed`.
    sharePolicy: detail.sharePolicy,
    hasFamilyPack: !!ownFamilyOrg && familyPackOrg === ownFamilyOrg,
  });
  // KEY-556: why a sharing form is shut, for the panels to show in its place. Only ever for a dial admin —
  // `allowed` is the policy alone, never the caller's role, so a Reader must not be told their organisation
  // closed a door they never had. Deliberately not recomputed from `authority` + `managesInstances`.
  const refusalFor = (channel: ShareChannelName) => (isDialAdmin ? shareRefusal(detail.sharePolicy, channel) : null);
  const onSettingsRoute = location.pathname.includes("/settings");
  const runWelcomeAction = (action: WelcomeAction) => {
    if (!detail) return;
    const step = welcomeStep(action, detail.id);
    closeWelcome();
    if (!step) return;
    if (step.type === "guide") startGuide(step.guideId);
    else if (step.type === "navigate") navigate(step.to, { state: location.state });
    else if (step.type === "link") window.open(step.href, "_blank", "noopener,noreferrer");
    else {
      if (onSettingsRoute) navigate(`/instances/${detail.id}`, { state: location.state });
      setWelcomeTab(step.tab as WorkspaceTab);
    }
  };
  const settingsSection = resolveSection(routeSection, settingsItems);
  const dialPath = `/instances/${id}`;

  // Someone who may open no settings at all — or who bookmarked a settings URL and has since lost the
  // grant that justified it — belongs back on the dial, not on an empty shell.
  // Not on the last dial's permissions, though: while the next one loads, that call waits for the right dial.
  if (onSettingsRoute && settingsSection === null && !stale) {
    return <Navigate to={dialPath} replace />;
  }

  // Settings are the overview's last tab (they used to be a page of their own). The URL is kept working
  // because these links get bookmarked and shared: arriving on /settings/:section opens the tab on that
  // section. Moving BETWEEN sections rewrites the URL too, so a link copied from the address bar still
  // points at what is on screen; moving to another tab is left alone, because the tab strip is the
  // workspace's own state and a dial is not worth a history entry per tab.
  const settingsSlot = settingsItems.length > 0 && settingsSection
    ? (
      <DialSettingsPanel
        items={settingsItems}
        section={settingsSection}
        onSelect={(next) => navigate(`/instances/${id}/settings/${next}`, { replace: true, state: location.state })}
      >
        {settingsSection === "members" && (
          <MembersSection
            noun={noun}
            instanceId={viewId}
            members={detail.members}
            sections={detail.sections.map((sec) => ({
              key: sec.key,
              name: sec.label,
              segments: sec.segments.map((g) => ({ key: g.key, name: g.name })),
            }))}
            canManage={isDialAdmin}
            canGrantAdmin={managesInstances}
            canAddOrgMembers={!!me?.capabilities.includes("org.members.manage")}
            memberRefusal={refusalFor("member")}
            onChanged={load}
          />
        )}
        {settingsSection === "whoswho" && (
          <WhosWhoSection
            instanceId={viewId}
            people={detail.people ?? []}
            members={detail.members}
            canManage={isDialAdmin}
            onChanged={load}
          />
        )}
        {settingsSection === "contacts" && (
          <ContactsSection
            instanceId={viewId}
            contacts={detail.contacts ?? []}
            canManage={isDialAdmin}
            onChanged={load}
          />
        )}
        {settingsSection === "calendars" && <CalendarsSection instanceId={viewId} isDialAdmin={isDialAdmin} onChanged={load} />}
        {settingsSection === "licensing" && <LicensingSection instanceId={viewId} />}
        {settingsSection === "family" && ownFamilyOrg && <FamilyCard organisationId={ownFamilyOrg} />}
        {settingsSection === "guests" && (
          <GuestsPanel
            instanceId={viewId}
            sections={detail.sections.map((sec) => ({
              key: sec.key,
              name: sec.label,
              // KEY-852: single items only where the dial offers them; otherwise the action point is the leaf.
              segments: sec.segments.map((g) => ({
                key: g.key,
                name: g.name,
                items: detail.offersItemScopedGuests ? g.items.map((i) => ({ key: i.key, name: i.name })) : undefined,
              })),
            }))}
            attributes={detail.nodeAttributes ?? []}
            refusal={refusalFor("guest")}
          />
        )}
        {settingsSection === "roles" && (
          detail.isGroup ? (
            // KEY-641: a group's statuses are rolled up from its subdials and never set on it, so there is
            // no role table to show here — just where to actually find one.
            <SettingsCard
              title="Status roles"
              subtitle="This is a group — statuses roll up from its subdials. Roles are set on each one, not here."
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {(detail.children ?? []).map((child) => (
                  <button
                    key={child.childInstanceId}
                    type="button"
                    className="kd-row-hover"
                    onClick={() => navigate(`/instances/${child.childInstanceId}/settings/roles`, {
                      state: {
                        organisationId: navOrgId ?? undefined,
                        fromParentInstanceId: viewId,
                        fromParentName: detail.name,
                      },
                    })}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "none",
                      textAlign: "left",
                      cursor: "pointer",
                      background: "transparent",
                      color: "var(--text)",
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {child.name}
                    <Icon name="chevRight" size={14} style={{ flex: "none", color: "var(--text-faint)" }} />
                  </button>
                ))}
              </div>
            </SettingsCard>
          ) : (
            <StatusRolesPanel instanceId={viewId} dialName={detail.name} onChanged={load} />
          )
        )}
        {settingsSection === "structure" && (
          cust?.canCustomise ? (
            <EditStructurePanel
              noun={noun}
              guidanceLabel={dialLabel(detail, "guidance.item")}
              instanceId={viewId}
              instanceName={detail.name}
              view={cust}
              onChanged={load}
              docImportEnabled={itemImportEnabled}
              itemAttributes={detail.nodeAttributes ?? []}
              statusDimension={[...(detail.dimensions ?? [])].sort((a, b) => a.order - b.order)[0] ?? null}
            />
          ) : (
            // KEY-239: the reason, not an empty pane. This is the whole point of listing the section for
            // an admin who cannot edit it.
            <SettingsCard title="Structure editing is off" subtitle="Why you can't change this dial's structure">
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: "var(--text-muted)" }}>{structureNotice}</p>
            </SettingsCard>
          )
        )}
        {settingsSection === "display" && (
          <>
            <DialNamePanel instanceId={viewId} name={detail.name} onChanged={load} />
            <DisplaySection
              noun={noun}
              instanceId={viewId}
              hideEmptyEnabled={!!detail.hideEmptyEnabled}
              hideEmptyForMe={!!detail.hideEmptyForMe}
              sectionEdgeSplitEnabled={!!detail.sectionEdgeSplitEnabled}
              miloEnabled={!!detail.miloEnabled}
              miloHidden={!!detail.miloHidden}
              listTabLabel={listTabLabel(detail)}
              canHideAnything={vm ? hasEmptyNodes(vm) : false}
              onApplied={applyDisplaySetting}
            />
            {detail.daysLayout && <DaysLayoutCard instanceId={viewId} layout={detail.daysLayout} onChanged={load} />}
          </>
        )}
        {settingsSection === "device" && (
          <>
            {autoLock && <AutoLockPanel instanceId={viewId} autoLock={autoLock} onChanged={load} />}
            <TimeZonePanel
              instanceId={viewId}
              timeZoneId={detail.timeZoneId ?? null}
              canEdit={detail.canEditTimeZone !== false}
              onChanged={load}
            />
          </>
        )}
        {settingsSection === "feeds" && (
          <FeedsSettingsPanel instanceId={viewId} dialName={detail.name} detail={detail} refusal={refusalFor("feed")} />
        )}
        {settingsSection === "insights" && <InsightsPanel instanceId={viewId} />}
      </DialSettingsPanel>
    )
    : undefined;

  return (
    <DialWorkspaceProvider services={workspaceServices} capabilities={capabilities}>
      {/* While the next dial loads, the last one stays where it is — dimmed, inert and marked busy, with the pill
          over it — rather than blanking the page, or, as it used to, carrying on looking live under the new
          dial's breadcrumb. `inert` takes it out of clicks, focus and the keyboard; the onScreen guards on the
          writes cover anything that calls in without one. */}
      {stale && (
        <div role="status" style={LOADING_PILL_STYLE}>
          Loading dial…
        </div>
      )}
      <div inert={stale} aria-busy={stale || undefined} style={stale ? STALE_DIAL_STYLE : undefined}>
        <DialBar
          vm={vm}
          org={summary?.organisationName ?? detail.organisationName}
          definitionName={summary?.definitionName ?? detail.definitionName}
          groupName={detail.parentInstanceName ?? fromParent?.name ?? fromGroup?.name ?? null}
          subs={subs}
          onSubsChanged={reloadSubs}
          meHasPhoneNumber={meHasPhoneNumber}
          onClearSimulatedRole={() => changeSimulatedRole(null)}
        />
        {/* Shown to EVERYONE who can open the dial, not just its admin: anyone entering work into a dial
            that is about to be deleted needs to know, whatever their role (KEY-283). */}
        {/* KEY-524: the SUBTREE, not this dial's own version — a checking group's children run their own
            latest versions, so gating on isTesting made the banner and the role toggle vanish the moment you
            opened a child, even though the server was still honouring the role header there. */}
        {(detail.inTestingSubtree ?? detail.isTesting) && (
          <TestingNotice
            simulatedRole={detail.simulatedRole ?? null}
            onSimulatedRoleChange={changeSimulatedRole}
            simulationBusy={simulationBusy}
            viaGroup={!detail.isTesting}
          />
        )}
        {/* KEY-560: the author's own test copy also gets its two ways out. Nobody else's dial ever matches. */}
        {detail.isTesting && <TestCopyActions instanceId={detail.id} />}
        {/* KEY-938: a demo dial says so to everyone, every time — before anybody drags a real document in. */}
        {detail.isDemo && (
          <DemoNotice
            onReset={isDialAdmin ? () => void resetDemo() : undefined}
            resetting={resettingDemo}
            error={resetDemoError}
          />
        )}
        {/* onApplied: applying an update now (KEY-240) reshapes the dial, so the whole workspace has to refetch.
            Keyed by dial, so the last dial's notice is never left showing while this one's is fetched.
            Every keyed sibling here needs its own prefix: DialWorkspace below is keyed by the bare dial id, and two
            siblings sharing a key make React leave the old one behind on each dial change — the follow prompt piled
            up one copy per dial visited. */}
        {isDialAdmin && <UpgradeNotice key={`upgrade:${viewId}`} instanceId={viewId} onApplied={load} noun={noun} itemNounFor={(sectionKey) => itemNouns(detail, sectionKey)} />}
        {/* KEY-592: asked once per person per dial; the server stops offering it once answered or followed.
            Keyed by dial, like the notice above, so a dismissal on one dial never hides the next one's. */}
        {/* KEY-743: a self-serve buyer whose organisation we named from their email address is asked to check it.
            One question at a time, and this one first: while it shows, the follow and add-people prompts below
            wait (they come back once it is answered or put away). Not keyed by dial — it is about the
            organisation, and answering it on one dial answers it everywhere. */}
        {orgNamePrompt.show && me?.organisationName && (
          <OrgNamePrompt
            key={`org-name:${me.organisationId}`}
            organisationName={me.organisationName}
            onDismiss={orgNamePrompt.dismiss}
          />
        )}
        {/* KEY-814: the dial's welcome page comes first, before the one-off prompts below — they wait while it's up. */}
        {welcome && welcomeOpenFor === detail.id && (
          <WelcomePage
            key={`welcome:${detail.id}`}
            welcome={welcome}
            dialName={welcomeDialName}
            actions={(welcome.actions ?? []).filter((a) =>
              welcomeActionUsable(a, { isDialAdmin, canAddCalendar: detail.sections.some((s) => s.kind === "Days") }))}
            onAction={runWelcomeAction}
            onClose={closeWelcome}
          />
        )}
        {!orgNamePrompt.show && !welcomeOpenFor && detail.offerFollowPrompt && !detail.isTesting && (
          <FollowPrompt
            key={`follow:${detail.id}`}
            instanceId={detail.id}
            dialName={summary?.definitionName ?? detail.definitionName ?? detail.name}
            isGroup={!!detail.isGroup}
            noun={noun}
            onFollowed={reloadSubs}
          />
        )}
        {/* KEY-751: a dial that picks people is no use until Who's who has someone in it. Keyed by dial like the
            prompts above; it goes away for good once anyone is added. */}
        {!orgNamePrompt.show && !welcomeOpenFor && shouldOfferAddPeople(detail, { peopleEnabled, isDialAdmin }) && (
          <AddPeoplePrompt
            key={`add-people:${detail.id}`}
            instanceId={detail.id}
            onAdd={() => navigate(`/instances/${detail.id}/settings/whoswho`, { state: location.state })}
          />
        )}
        {/* KEY-815: a dial of days is empty until a calendar is on it. The card asks the server who can add one, and waits
            while the welcome page is up. */}
        {!orgNamePrompt.show && !welcomeOpenFor && !detail.isGroup && detail.sections.some((s) => s.kind === "Days") && (
          <CalendarSetupPrompt
            key={`calendar-setup:${detail.id}`}
            instanceId={detail.id}
            onAdd={() => navigate(`/instances/${detail.id}/settings/calendars`, { state: location.state })}
            onConnect={() => navigate("/settings#calendars")}
          />
        )}
        {addOnDay && (
          <AddOnDayDialog
            instanceId={detail.id}
            segmentKey={addOnDay.segmentKey}
            dayLabel={addOnDay.dayLabel}
            defaultStart={slotStartOf(addOnDay.segmentKey, detail.daysLayout)}
            sources={addOnDay.sources}
            itemWord={itemWordsForSegment(addOnDay.segmentKey, addOnDay.sectionKey).one}
            onJustDial={() => {
              const asked = addOnDay;
              setAddOnDay(null);
              void addDialItem(asked.segmentKey, asked.sectionKey).then(asked.resolve);
            }}
            onDone={(itemKey) => {
              const asked = addOnDay;
              setAddOnDay(null);
              if (!itemKey) {
                asked.resolve(null);
                return;
              }
              // Read back from the calendar already: reload so the dial shows it, then open it.
              void load().then(() => asked.resolve(itemKey));
            }}
          />
        )}
        {changing && (
          <AddOnDayDialog
            instanceId={detail.id}
            segmentKey={changing.segmentKey}
            dayLabel=""
            defaultStart={null}
            sources={[]}
            itemWord={itemWordsForSegment(changing.segmentKey).one}
            onJustDial={() => setChanging(null)}
            editing={changing.appointment}
            onDone={(itemKey) => {
              setChanging(null);
              if (itemKey) void load();
            }}
          />
        )}
        {/* KEY-754: only ever set for a dial admin who asked to see the hidden parts, on their own read. */}
        {detail.hiddenRevealExpiresAtUtc && (
          <HiddenRevealBanner key={`reveal:${detail.id}`} instanceId={detail.id} expiresAtUtc={detail.hiddenRevealExpiresAtUtc} onChanged={load} />
        )}
        <DialWorkspace
          key={detail.id}
          detail={detail}
          vm={vm}
          history={history}
          itemHistory={itemHistory}
          subs={subs}
          meUserId={me?.userId ?? null}
          meHasPhoneNumber={meHasPhoneNumber}
          canSet={canSet}
          allocation={allocation}
          canEditDocs={canEditDocs}
          canListDocs={canListDocs}
          canDownloadDocs={canDownloadDocs}
          canEditAttributes={canEditAttributes}
          attributeAccess={attributeAccess}
          canManageDocVersions={canManageDocVersions}
          canPost={canPost}
          canModerate={isDialAdmin}
          restricted={restricted}
          canEditCentreLogo={canEditCentreLogo}
          centreLogoNote={centreLogoNote}
          lastUpdated={lastUpdated}
          error={error}
          itemEditPolicy={itemEditPolicy}
          controller={controller}
          // KEY-606: "Whole dial" — the step that shows the entire dial rather than one section of it.
          overviewCrumbLabel="Whole dial"
          // KEY-458: the Overview tab leads with the definition's name — what this dial *is* — falling back
          // to the instance's own name when the summary hasn't loaded or doesn't carry one.
          overviewTitle={summary?.definitionName ?? detail.definitionName ?? detail.name}
          overviewNote={
            <>
              {welcome && <GettingStartedButton onOpen={() => setWelcomeOpenFor(detail.id)} />}
              <HiddenSectionsNote instanceId={viewId} view={cust} noun={noun} revealing={!!detail.hiddenRevealExpiresAtUtc} onChanged={load} />
            </>
          }
          parentCrumb={
            fromParent
              ? { label: "Back to group", title: fromParent.name, onClick: () => navigate(`/instances/${fromParent.id}`, { state: navOrgId ? { organisationId: navOrgId } : undefined }) }
              : fromGroup
                ? { label: "Back to group", title: fromGroup.name, onClick: () => navigate(`/groups/${fromGroup.id}`) }
                : detail.parentInstanceId && detail.parentInstanceName
                  // Reached without the drill-in's navigation state (a reload, a link): the server still names the group.
                  ? { label: "Back to group", title: detail.parentInstanceName, onClick: () => navigate(`/instances/${detail.parentInstanceId}`) }
                  : undefined
          }
          initialSectionKey={navState?.focusSectionKey ?? linkTarget.section}
          initialSegmentKey={navState?.focusSegmentKey ?? linkTarget.segment}
          settingsSlot={settingsSlot}
          initialPanelTab={onSettingsRoute ? "settings" : welcomeTab}
        />
      </div>
    </DialWorkspaceProvider>
  );
}
